import { useMemo } from 'react';
import { Handle, Position } from '@xyflow/react';
import { useShallow } from 'zustand/react/shallow';
import { useNodeStore } from '@/stores/node-store';
import type { NodeStruct } from '@/types/node-types';
import { ConnectionType } from '@/types/edge-types';
import {
  REPEAT_SOURCE_HANDLE,
  repeatFeedbackHandleId,
} from '@/pages/workflow/helper/repeat-control';
import { canBeRepeated } from '@/pages/workflow/helper/repeat-loop';
import { feedbackSlotsFor } from '@/pages/workflow/helper/repeat-author';

interface RepeatFeedbackHandleProps {
  nodeId: string;
  struct: NodeStruct;
  isLocked?: boolean;
}

const AMBER = '#d97706';

/**
 * Where a loop's feedback lands: one target handle per declared feedback
 * slot on a node that can be repeated. Hidden until a "repeat" handle is
 * being dragged or a repeat edge is connected, so ordinary nodes stay clean.
 */
export function RepeatFeedbackHandle({
  nodeId,
  struct,
  isLocked = false,
}: RepeatFeedbackHandleProps) {
  const { activeConnect, groupedNodes, connectedKey } = useNodeStore(
    useShallow((state) => ({
      activeConnect: state.activeConnect,
      groupedNodes: state.groupedNodes,
      connectedKey: state.edges
        .filter(
          (edge) =>
            edge.target === nodeId && edge.edge_type === ConnectionType.REPEAT,
        )
        .map((edge) => edge.targetHandle)
        .join('|'),
    })),
  );
  const catalog = useMemo(
    () => Object.values(groupedNodes).flat(),
    [groupedNodes],
  );
  const connectedHandles = connectedKey ? connectedKey.split('|') : [];
  if (!canBeRepeated(struct, catalog)) return null;
  const slots = feedbackSlotsFor(struct, catalog);
  if (slots.length === 0) return null;
  const dragActive =
    activeConnect?.side === 'source' &&
    activeConnect.handleId === REPEAT_SOURCE_HANDLE &&
    activeConnect.nodeId !== nodeId;

  return (
    <>
      {slots.map((slot, index) => {
        const handleId = repeatFeedbackHandleId(slot);
        const visible = dragActive || connectedHandles.includes(handleId);
        return (
          <Handle
            key={handleId}
            id={handleId}
            type='target'
            position={Position.Top}
            isConnectable={!isLocked}
            isConnectableStart={false}
            title={`Feedback input "${slot.target_id}": drop the repeat edge here`}
            data-testid={`repeat-feedback-${nodeId}-${slot.target_id}`}
            style={{
              top: -7,
              left: 28 + index * 20,
              transform: 'none',
              width: 14,
              height: 14,
              borderRadius: 4,
              backgroundColor: AMBER,
              color: AMBER,
              border: '2px solid var(--color-card)',
              opacity: visible ? 1 : 0,
              pointerEvents: visible ? 'auto' : 'none',
            }}
            className='repeat-feedback-handle transition-opacity duration-150'
            isValidConnection={(connection) =>
              connection.sourceHandle === REPEAT_SOURCE_HANDLE
            }
          />
        );
      })}
    </>
  );
}
